import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, FileText, Download, Trash2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLOR = {
  approved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  pending:  'bg-amber-100 text-amber-700 border-amber-200',
  sent:     'bg-blue-100 text-blue-700 border-blue-200',
  done:     'bg-teal-100 text-teal-700 border-teal-200',
  rejected: 'bg-rose-100 text-rose-700 border-rose-200',
  expired:  'bg-slate-200 text-slate-600 border-slate-300',
};

const STATUS_OPTIONS = ['pending', 'sent', 'approved', 'done', 'rejected', 'expired'];

const fmtD = (s) => { if (!s) return ''; const d = new Date(s); return isNaN(d.getTime()) ? s : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); };
const isExpired = (s) => { if (!s) return false; const d = new Date(s); return !isNaN(d.getTime()) && d.getTime() < Date.now(); };
const tripLabelOf = (t) => `${t.destination || 'Trip'} · ${t.displayTripId || t.tripId || 'ID pending'}`;

/**
 * Visa documents. Trip-aware:
 * - On a Traveller page (no `tripId` prop): lists ALL the traveller's visas,
 *   shows each visa's linked trip (or "Not linked to a trip"), and the upload
 *   form offers an optional Trip selector.
 * - In a Trip context (`tripId` prop set): lists ONLY that trip's visas and
 *   auto-links new uploads to that trip (so a multi-trip traveller's visa
 *   associates with the correct trip, not all of them).
 */
export default function TravellerVisaDocs({ ucc, tripId = '', tripLabel = '', trips = [] }) {
  const inTripContext = !!tripId;
  const [docs, setDocs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    tripId: tripId || '', country: '', visaType: '', visaNumber: '',
    validFrom: '', validTo: '', status: 'pending', notes: '',
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const tripName = (tid) => {
    if (!tid) return 'Not linked to a trip';
    const t = trips.find((x) => (x.tripId || x.id) === tid);
    return t ? tripLabelOf(t) : tid;
  };

  const load = async () => {
    try {
      const qp = inTripContext ? `clientUCC=${ucc}&tripId=${encodeURIComponent(tripId)}` : `clientUCC=${ucc}`;
      const r = await axios.get(`${BACKEND_URL}/api/visa-docs?${qp}`);
      setDocs((r.data && r.data.items) || []);
    } catch (e) { /* silent */ }
  };

  useEffect(() => { if (ucc) load(); /* eslint-disable-next-line */ }, [ucc, tripId]);

  const upload = async () => {
    if (!file) { toast.error('Choose a file first'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('clientUCC', ucc);
      // Trip context forces the current trip; traveller page uses the picker.
      const effectiveTripId = inTripContext ? tripId : (form.tripId || '');
      Object.entries(form).forEach(([k, v]) => { if (k !== 'tripId') fd.append(k, v); });
      fd.append('tripId', effectiveTripId);
      await axios.post(`${BACKEND_URL}/api/visa-docs/upload`, fd);
      toast.success('Visa document uploaded');
      setShowForm(false);
      setFile(null);
      setForm({ tripId: tripId || '', country: '', visaType: '', visaNumber: '', validFrom: '', validTo: '', status: 'pending', notes: '' });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this visa document?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/visa-docs/${id}`);
      toast.success('Deleted');
      load();
    } catch { toast.error('Delete failed'); }
  };

  /**
   * Inline status change handler — lets the user mark a visa as
   * sent/approved/done/etc directly from the row WITHOUT re-uploading.
   * Persists via PUT /api/visa-docs/{id} which also bubbles the status
   * back to the linked traveller + trip docs (handled server-side).
   */
  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${BACKEND_URL}/api/visa-docs/${id}`, { status });
      toast.success(`Marked as ${status}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Status update failed');
    }
  };

  // BATCH 1 — visa scans are sensitive; no public URL. Ask the backend for a
  // short-lived (5-min) HMAC signed URL (record + permission checked +
  // audited), then open it. Open a blank tab synchronously so the async
  // fetch isn't blocked as a popup.
  const openSigned = async (visaDocId) => {
    const w = window.open('', '_blank');
    try {
      const r = await axios.get(`${BACKEND_URL}/api/visa-docs/${visaDocId}/sign`, { withCredentials: true });
      const url = `${BACKEND_URL}${r.data.url}`;
      if (w) w.location = url; else window.open(url, '_blank');
    } catch (err) {
      if (w) w.close();
      const msg = err?.response?.data?.detail || 'You do not have access to view this visa document';
      toast.error(typeof msg === 'string' ? msg : 'Cannot view visa document');
    }
  };

  return (
    <Card className="p-6 sm:p-8 bg-white/80 backdrop-blur-xl border-2 border-slate-100 rounded-3xl shadow-lg" data-testid="visa-docs-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-heading font-bold text-slate-900 flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary-700" /> Visa Documents
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            {inTripContext
              ? `Visas linked to this trip${tripLabel ? ` · ${tripLabel}` : ''}. New uploads auto-link here.`
              : 'Stored as-is, no rebranding applied.'}
          </p>
        </div>
        <Button onClick={() => setShowForm(!showForm)} variant="outline" size="sm" className="rounded-lg" data-testid="add-visa-doc-btn">
          <Plus className="h-4 w-4 mr-1.5" /> {showForm ? 'Cancel' : 'Add Visa Doc'}
        </Button>
      </div>

      {showForm && (
        <div className="mb-6 bg-slate-50 rounded-xl p-4 border border-slate-200 space-y-3" data-testid="visa-upload-form">
          {inTripContext ? (
            <div className="text-xs text-teal-700 bg-teal-50 border border-teal-100 rounded-lg px-3 py-2" data-testid="visa-trip-locked">
              This visa will be linked to <span className="font-semibold">{tripLabel || tripId}</span>.
            </div>
          ) : (
            <div>
              <Label className="text-xs">Link to trip (optional)</Label>
              <select
                value={form.tripId}
                onChange={(e) => setForm({ ...form, tripId: e.target.value })}
                className="w-full h-9 rounded-md border border-slate-200 px-2 text-sm bg-white"
                data-testid="visa-trip-select"
              >
                <option value="">Not linked to a trip</option>
                {trips.map((t) => (
                  <option key={t.tripId || t.id} value={t.tripId || t.id}>{tripLabelOf(t)}</option>
                ))}
              </select>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Country</Label>
              <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="e.g., France" className="h-9" data-testid="visa-country" />
            </div>
            <div>
              <Label className="text-xs">Visa Type</Label>
              <Input value={form.visaType} onChange={(e) => setForm({ ...form, visaType: e.target.value })} placeholder="e.g., Schengen / Tourist" className="h-9" data-testid="visa-type" />
            </div>
            <div>
              <Label className="text-xs">Visa Number</Label>
              <Input value={form.visaNumber} onChange={(e) => setForm({ ...form, visaNumber: e.target.value })} className="h-9" />
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full h-9 rounded-md border border-slate-200 px-2 text-sm bg-white" data-testid="visa-status-select">
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Valid From</Label>
              <Input type="date" value={form.validFrom} onChange={(e) => setForm({ ...form, validFrom: e.target.value })} className="h-9" />
            </div>
            <div>
              <Label className="text-xs">Valid To</Label>
              <Input type="date" value={form.validTo} onChange={(e) => setForm({ ...form, validTo: e.target.value })} className="h-9" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Notes</Label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional remarks" className="h-9" />
          </div>
          <div className="flex items-center gap-2">
            <Input type="file" accept="application/pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="h-9 flex-1" data-testid="visa-file-input" />
            <Button onClick={upload} disabled={busy} size="sm" className="h-9 bg-teal-700 hover:bg-teal-800 text-white" data-testid="visa-upload-submit">
              <Upload className="h-3.5 w-3.5 mr-1.5" /> {busy ? 'Uploading…' : 'Upload'}
            </Button>
          </div>
        </div>
      )}

      {docs.length === 0 ? (
        <p className="text-sm text-slate-400 italic text-center py-6">No visa documents uploaded yet.</p>
      ) : (
        <div className="space-y-2">
          {docs.map((d) => (
            <div key={d.visaDocId} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-100 hover:bg-slate-50" data-testid={'visa-row-' + d.visaDocId}>
              <FileText className="h-5 w-5 text-slate-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-slate-900 text-sm truncate">{d.country || 'Visa'}</span>
                  <span className={'px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide border ' + (STATUS_COLOR[d.status] || STATUS_COLOR.pending)}>
                    {d.status}
                  </span>
                  {d.visaType && <span className="text-xs text-slate-500">• {d.visaType}</span>}
                </div>
                <p className="text-xs text-slate-500 mt-0.5 truncate">
                  {d.visaNumber && <>#{d.visaNumber} • </>}
                  {d.validTo
                    ? <span className={isExpired(d.validTo) ? 'text-rose-600 font-semibold' : 'text-slate-600'} data-testid={'visa-expiry-' + d.visaDocId}>
                        {isExpired(d.validTo) ? 'Expired' : 'Expires'} {fmtD(d.validTo)}
                      </span>
                    : d.fileName}
                </p>
                {!inTripContext && (
                  <p className="text-[11px] mt-0.5 truncate" data-testid={'visa-trip-' + d.visaDocId}>
                    <span className={d.tripId ? 'text-teal-600' : 'text-slate-400 italic'}>{tripName(d.tripId)}</span>
                  </p>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {/* Inline status updater — mark visa as sent/approved/done
                    without re-uploading the file. */}
                <select
                  value={d.status || 'pending'}
                  onChange={(e) => updateStatus(d.visaDocId, e.target.value)}
                  className="h-8 rounded-md border border-slate-200 px-2 text-xs bg-white text-slate-700"
                  data-testid={'visa-status-update-' + d.visaDocId}
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                  ))}
                </select>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => openSigned(d.visaDocId)}
                  data-testid={'visa-download-' + d.visaDocId}
                >
                  <Download className="h-4 w-4 text-slate-500" />
                </Button>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-400 hover:text-rose-600" onClick={() => remove(d.visaDocId)} data-testid={'visa-delete-' + d.visaDocId}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
